/**
 * PhaseTracker — the task's progress at a glance (milestones + liveness; mechanics one click away).
 *
 * Plans of at most `TRACKER_MAX_ITEMS` items get the row of phases (Plan → Approve → Build (n/m) →
 * Check → Ready to save) and one line per component. Bigger plans get `BuildSummary`: a summary
 * bar, a "Needs attention" group first, and the railway `SignalBoard` behind a "Dependencies" toggle.
 *
 * Status is never colour alone: every state has its own glyph AND words (an accessible name on the
 * glyph, plus visible text). Neutral grey by default, the accent for what is happening now, and a
 * strong colour only for failure. No motion here: the shimmer belongs to the turn story alone.
 */
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { SignalBoard } from "./SignalBoard";
import {
  buildTrackerModel,
  COMPONENT_GLYPH,
  type ComponentState,
  type TrackerComponent,
  type TrackerPhase,
  type TrackerPhaseState,
} from "./trackerModel";
import { isTerminalState } from "./views";

const PHASE_GLYPH: Record<TrackerPhaseState, string> = { done: "✓", active: "◐", waiting: "○", failed: "!", stopped: "⊘", todo: "○" };
const PHASE_WORDS: Record<TrackerPhaseState, string> = {
  done: "done",
  active: "in progress",
  waiting: "waiting for you",
  failed: "needs attention",
  stopped: "stopped",
  todo: "not started",
};

const COMPONENT_TONE: Record<ComponentState, string> = {
  done: "text-muted-foreground",
  active: "text-primary",
  waiting: "text-muted-foreground opacity-70",
  attention: "font-semibold text-[var(--ma-state-err-text)]",
  skipped: "text-muted-foreground line-through opacity-60",
};

function Glyph({ glyph, label, className }: { glyph: string; label: string; className?: string }) {
  return (
    <span role="img" aria-label={label} className={cn("inline-block w-4 shrink-0 text-center font-mono text-[12px] leading-none", className)}>
      {glyph}
    </span>
  );
}

function PhaseRow({ phases }: { phases: TrackerPhase[] }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label="Task progress" data-lane-v2-tracker-phases="">
      {phases.map((p) => (
        <li
          key={p.key}
          data-tracker-phase={p.key}
          data-tracker-phase-state={p.state}
          className={cn(
            "inline-flex items-center gap-1 text-[12px]",
            p.state === "done" && "text-muted-foreground",
            (p.state === "active" || p.state === "waiting") && "font-medium text-primary",
            p.state === "failed" && "font-semibold text-[var(--ma-state-err-text)]",
            (p.state === "todo" || p.state === "stopped") && "text-muted-foreground opacity-70",
          )}
        >
          <Glyph glyph={PHASE_GLYPH[p.state]} label={PHASE_WORDS[p.state]} />
          <span>{p.label}</span>
        </li>
      ))}
    </ol>
  );
}

function ComponentLine({ c }: { c: TrackerComponent }) {
  return (
    <li data-tracker-component={c.state} className={cn("flex items-baseline gap-2 text-[12px]", COMPONENT_TONE[c.state])}>
      <Glyph glyph={COMPONENT_GLYPH[c.state]} label={c.detail} />
      <span className="min-w-0 break-words">{c.label}</span>
      <span className="shrink-0 text-[11px] opacity-80">· {c.detail}</span>
    </li>
  );
}

/** Components in plan order — at most `TRACKER_MAX_ITEMS`, so the whole list is the view. */
export function PhaseTracker({ snapshot, className }: { snapshot: TaskSnapshot; className?: string }) {
  const m = buildTrackerModel(snapshot);
  return (
    <section aria-label="Progress" data-lane-v2-tracker="" className={cn("space-y-2 rounded-md border border-border px-3.5 py-2.5", className)}>
      <PhaseRow phases={m.phases} />
      {m.components.length > 0 && (
        <ul className="flex flex-col gap-0.5 border-l-2 border-border pl-3" aria-label="Components">
          {m.components.map((c) => (
            <ComponentLine key={c.key} c={c} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Most "Needs attention" lines listed before folding the rest into a count. */
const ATTENTION_SHOWN = 10;

/** For plans above the cutoff: the summary first, what needs you next, the dependency board on request. */
export function BuildSummary({
  snapshot,
  onViewItemBody,
  className,
}: {
  snapshot: TaskSnapshot;
  onViewItemBody?: (workItemId: string) => void;
  className?: string;
}) {
  const [showDeps, setShowDeps] = useState(false);
  const m = buildTrackerModel(snapshot);
  const attention = m.components.filter((c) => c.state === "attention");
  const canShowBoard = !isTerminalState(snapshot.state);
  const pct = m.total > 0 ? Math.round((m.built / m.total) * 100) : 0;
  return (
    <section aria-label="Progress" data-lane-v2-build-summary="" className={cn("space-y-2 rounded-md border border-border px-3.5 py-2.5", className)}>
      <PhaseRow phases={m.phases} />
      <div className="space-y-1">
        <p className="text-[12px] text-foreground" data-lane-v2-summary-text="">
          {m.built} / {m.total} built
          {m.needsAttention > 0 && (
            <>
              {" "}
              · <span className="font-semibold text-[var(--ma-state-err-text)]">{m.needsAttention} need attention</span>
            </>
          )}
        </p>
        <div
          role="progressbar"
          aria-label="Components built"
          aria-valuemin={0}
          aria-valuemax={m.total}
          aria-valuenow={m.built}
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {attention.length > 0 && (
        <div data-lane-v2-needs-attention="">
          <h4 className="text-[12px] font-semibold text-[var(--ma-state-err-text)]">Needs attention</h4>
          <ul className="mt-0.5 flex flex-col gap-0.5 border-l-2 border-ma-err-line pl-3">
            {attention.slice(0, ATTENTION_SHOWN).map((c) => (
              <ComponentLine key={c.key} c={c} />
            ))}
            {attention.length > ATTENTION_SHOWN && (
              <li className="text-[12px] text-muted-foreground">and {attention.length - ATTENTION_SHOWN} more</li>
            )}
          </ul>
        </div>
      )}
      {canShowBoard && (
        <div>
          <button
            type="button"
            aria-expanded={showDeps}
            onClick={() => setShowDeps((v) => !v)}
            data-lane-v2-deps-toggle=""
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span aria-hidden>{showDeps ? "▾" : "▸"}</span>
            Dependencies
          </button>
          {showDeps && <SignalBoard key={snapshot.task_id} snapshot={snapshot} onViewItemBody={onViewItemBody} className="mt-2" />}
        </div>
      )}
    </section>
  );
}
